import { useFeatureDetectionContext } from "@/components/common/feature-detection-context";
import { selectHasCreatedNewAnnotation } from "@/store/create-annotation-slice";
import { selectIsLocationMarkerCreationEnabled } from "@/store/location-marker-slice";
import {
  selectIsAxisLocked,
  selectLivePointsCount,
  selectPanoMeasurementToolState,
} from "@/store/measurement-tool-selector";
import { PanoMeasurementToolState } from "@/store/measurement-tool-slice";
import { selectModeName } from "@/store/mode-selectors";
import { selectNumberOfSessionAnalyses } from "@/store/point-cloud-analysis-tool-selector";
import { useAppSelector } from "@/store/store-hooks";
import { selectActiveTool } from "@/store/ui/ui-selectors";
import { ToolName } from "@/store/ui/ui-slice";
import {
  ContextualBanner,
  FaroText,
  neutral,
} from "@faro-lotv/app-component-toolbox";
import { isMacOS } from "@faro-lotv/foundation";
import { Box, SxProps, Theme, useMediaQuery } from "@mui/material";
import { PropsWithChildren, ReactNode, type JSX } from "react";
import { AvailableAnnotationTools } from "../../r3f/renderers/annotations/annotations-types";

type Props = {
  /** Additional styling props */
  sx?: SxProps<Theme>;
};

type GeneralHelpBannerProps = Props & {
  /** Text to show in the banner */
  text: ReactNode;

  /** Name of the tool to check. When the tool is activated, the banner will be shown */
  toolToCheck: ToolName;

  /** If true, then the banner will be hidden. @default false */
  hidden?: boolean;
};

function GeneralHelpBanner({
  text,
  toolToCheck,
  hidden,
  sx,
}: GeneralHelpBannerProps): JSX.Element | null {
  const activeTool = useAppSelector(selectActiveTool);

  if (activeTool !== toolToCheck || hidden) return null;
  return <ContextualBanner sx={sx}>{text}</ContextualBanner>;
}

type StepsHelpBannerProps = Omit<GeneralHelpBannerProps, "text"> & {
  /** Messages of the guide, in order */
  steps: ReactNode[];

  /** Index of the step to show, owned by the caller. The banner is hidden past the last step */
  currentStep: number;
};

/**
 * @returns a help banner guiding the user step by step, showing one message at a time
 */
export function StepsHelpBanner({
  steps,
  currentStep,
  hidden = false,
  ...rest
}: StepsHelpBannerProps): JSX.Element | null {
  return (
    <GeneralHelpBanner
      {...rest}
      text={steps[currentStep]}
      hidden={hidden || currentStep >= steps.length}
    />
  );
}

/** @returns a keyboard key rendered as a key cap */
function Key({ children }: PropsWithChildren): JSX.Element {
  return (
    <Box
      component="kbd"
      sx={{
        px: 0.75,
        mx: 0.25,
        border: `1px solid ${neutral[500]}`,
        borderRadius: "4px",
        fontFamily: "inherit",
        fontSize: "0.85em",
        fontWeight: "bold",
      }}
    >
      {children}
    </Box>
  );
}

/**
 * @returns a help message banner guiding the user through the measurement being taken, replaced by
 * a confirmation while SHIFT or CMD/CTRL is held
 */
export function MeasurementHelpBanner({ sx }: Props): JSX.Element | null {
  const livePointsCount = useAppSelector(selectLivePointsCount);
  const isAxisLocked = useAppSelector(selectIsAxisLocked);
  const panoMeasureState = useAppSelector(selectPanoMeasurementToolState);
  const isSheet = useAppSelector(selectModeName) === "sheet";
  const isTouch = useMediaQuery("(pointer: coarse)");
  const { edgeCornerDetection } = useFeatureDetectionContext();

  // Snapping runs only on point clouds and panoramas with depth (walk and overview modes)
  const canSnap =
    !isTouch && !isSheet && panoMeasureState === PanoMeasurementToolState.None;

  let modifierHint: string | undefined;
  if (isAxisLocked && livePointsCount > 0) {
    modifierHint = isSheet
      ? "Locked to the X or Y axis"
      : "Locked to horizontal or vertical";
  } else if (edgeCornerDetection && canSnap) {
    modifierHint = "Snapping to edges and corners";
  }

  const steps = [
    <FaroText key="first-point" variant="bodyL" dark>
      {isTouch ? "Tap" : "Click"} to place the first point
    </FaroText>,
    <FaroText key="more-points" variant="bodyL" dark>
      {isTouch ? (
        "Tap to add more points"
      ) : (
        <>
          Click to add more points · Hold <Key>SHIFT</Key> to lock the direction
          {canSnap && (
            <>
              {" "}
              · <Key>{isMacOS() ? "CMD" : "CTRL"}</Key> to snap to edges
            </>
          )}
        </>
      )}
    </FaroText>,
    <FaroText key="finish" variant="bodyL" dark>
      {isTouch ? (
        "Tap the first point to close the area"
      ) : (
        <>
          <Key>ESC</Key> to finish · Click the first point to close the area ·{" "}
          <Key>BACKSPACE</Key> to undo
        </>
      )}
    </FaroText>,
  ];

  return modifierHint ? (
    <GeneralHelpBanner
      text={
        <FaroText variant="bodyL" dark>
          {modifierHint}
        </FaroText>
      }
      toolToCheck={ToolName.measurement}
      sx={sx}
    />
  ) : (
    <StepsHelpBanner
      steps={steps}
      currentStep={Math.min(livePointsCount, steps.length - 1)}
      toolToCheck={ToolName.measurement}
      sx={sx}
    />
  );
}

interface CreateAnnotationHelpBannerProps extends Props {
  /** Available tool/s to which the help text needs to be updated */
  availableTool: AvailableAnnotationTools;
}

/**
 * @returns a help message banner which can be used for informing how to create a new Annotation
 */
export function CreateAnnotationHelpBanner({
  availableTool,
  sx,
}: CreateAnnotationHelpBannerProps): JSX.Element | null {
  const hasCreatedNewAnnotation = useAppSelector(selectHasCreatedNewAnnotation);

  let helpText = "";
  switch (availableTool) {
    case AvailableAnnotationTools.rectangle:
      helpText = "Click and drag to draw the annotation rectangle.";
      break;
    case AvailableAnnotationTools.singlePoint:
      helpText =
        "Click the point at which you want to insert a point annotation.";
      break;
    case AvailableAnnotationTools.all:
      helpText =
        "Click to insert a point annotation or click and drag to insert an area annotation.";
      break;
  }
  return (
    <GeneralHelpBanner
      text={helpText}
      toolToCheck={ToolName.annotation}
      hidden={hasCreatedNewAnnotation}
      sx={sx}
    />
  );
}

/**
 * @returns a help message banner which can be used for informing how the analysis can be performed
 */
export function AnalysisHelpBanner({ sx }: Props): JSX.Element | null {
  const isFirstAnalysis = useAppSelector(selectNumberOfSessionAnalyses) === 0;

  return (
    <GeneralHelpBanner
      text="Close the polygon by clicking the first point."
      toolToCheck={ToolName.analysis}
      hidden={!isFirstAnalysis}
      sx={sx}
    />
  );
}

/**
 * @returns a help message banner for the edit waypoints tool
 */
export function EditWaypointsHelpBanner({ sx }: Props): JSX.Element {
  const isLocationMarkerCreationEnabled = useAppSelector(
    selectIsLocationMarkerCreationEnabled,
  );

  return (
    <GeneralHelpBanner
      text={
        isLocationMarkerCreationEnabled
          ? "Click to add location markers"
          : "Drag waypoints to reposition them"
      }
      toolToCheck={ToolName.editWaypoints}
      sx={sx}
    />
  );
}
